import { buildFeatureFlags } from "@packages/feature-flags";
import { Product, ResultsInfo } from "./types/types";
import { layoutMap as listLayouts } from "./product-list-layouts/layoutMap";
import { raise } from "./utils/raise";
import defaultFlagSettings from "./flags/defaultFlagSettings.json";
import { useSocialProofMessaging } from "./product-card-components/SocialProofMessaging/useSocialProofMessaging";
import { SocialProofMessagesProvider } from "./contexts/SocialProofMessagesContext/SocialProofMessagesContext";
import { FeatureFlagsProvider } from "./flags/flags";
import {
  productListConfigMap,
  ProductListConfigName,
} from "./configs/productListConfigMap";
import { REMOVE_PRODUCT } from "./utils/events";
import { useEffect, useState } from "react";

type ProductListProps = {
  products: Product[];
  configName: ProductListConfigName;
  experiencesRawString?: string;
  resultsInfo: ResultsInfo;
  heading?: {
    main: string;
    subHeading?: string;
  };
  flags?: Record<string, unknown>;
};

export function ProductList({
  products,
  configName,
  experiencesRawString,
  resultsInfo,
  heading,
  flags,
}: ProductListProps) {
  const config =
    productListConfigMap[configName] ||
    raise(`Couldn't find product-list config "${configName}"`);

  const [visibleProducts, setVisibleProducts] = useState(products);

  useEffect(
    function updateVisibleProductsFromProps() {
      setVisibleProducts(products);
    },
    [products],
  );

  useEffect(function subscribeToRemoveProductEvents() {
    const handleRemoveProduct = (event: Event) => {
      const removeEvent = event as CustomEvent<{ productId: string }>;
      const removedProductId = removeEvent.detail?.productId;

      if (!removedProductId) {
        return;
      }

      setVisibleProducts((currentProducts) =>
        currentProducts.filter(
          (product) => product.aliases?.pimProductId !== removedProductId,
        ),
      );
    };

    window.addEventListener(REMOVE_PRODUCT, handleRemoveProduct);

    return function unsubscribeFromRemoveProductEvents() {
      window.removeEventListener(REMOVE_PRODUCT, handleRemoveProduct);
    };
  }, []);

  const { name: listLayoutName, props: listLayoutProps } =
    config["product-list-layout"];

  const ListLayout =
    listLayouts[listLayoutName] ||
    raise(`Couldn't find product-list layout "${listLayoutName}"`);

  const combinedFlags = buildFeatureFlags({
    defaultFlagValues: defaultFlagSettings,
    configFlags: config.flags,
    parameterFlags: flags,
  });

  const socialProofComponent = config["product-card-slots"].banner?.filter(
    (bslot) => bslot.name === "SocialProofMessaging",
  );

  const socialProofMessages = useSocialProofMessaging({
    resultsInfo,
    products: visibleProducts,
    appId: `${(socialProofComponent && socialProofComponent[0]?.props?.appId) || ""}`,
  });

  return (
    <FeatureFlagsProvider
      flags={combinedFlags}
      experiencesRawString={experiencesRawString}
      id="product-list"
    >
      <SocialProofMessagesProvider messages={socialProofMessages}>
        <ListLayout
          config={config}
          products={visibleProducts}
          configProps={listLayoutProps}
          resultsInfo={resultsInfo}
          heading={heading}
        />
      </SocialProofMessagesProvider>
    </FeatureFlagsProvider>
  );
}
